import Loading, { LoadingBall } from '@ingka/loading';
import Text from '@ingka/text';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import { merge } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePartialFormModule } from '../../utils/use-partial-form-module';
import type { FormModuleProps, ModuleTransformer } from '../types';
import type { SwipeRulesPartialFormType } from './partial-forms/SwipeRulesPartialForm';
import {
  SwipeRulesPartialForm,
  SwipeRulesPartialFormTransformer,
} from './partial-forms/SwipeRulesPartialForm';
import type { SwipeTranslationsPartialFormType } from './partial-forms/SwipeTranslationsPartialForm';
import {
  SwipeTranslationsPartialForm,
  SwipeTranslationsTransformer,
} from './partial-forms/SwipeTranslationsPartialForm';

export const MODULE_NAME = 'swipe';

export type SwipeFormType = SwipeRulesPartialFormType & SwipeTranslationsPartialFormType;

export const SwipeFormTransformer: ModuleTransformer<SwipeFormType> = {
  fromPayload: (args) => {
    return merge(
      SwipeRulesPartialFormTransformer.fromPayload(args),
      SwipeTranslationsTransformer.fromPayload(args)
    );
  },
  toPayload: (args) => {
    return merge(
      SwipeRulesPartialFormTransformer.toPayload(args),
      SwipeTranslationsTransformer.toPayload(args),
      {
        lottery: {
          dataSource: {
            dataSourceType: 'SWIPE',
          },
        },
      }
    );
  },
};

export type SwipeFormProps = FormModuleProps<SwipeFormType>;

export const SwipeForm: React.FC<SwipeFormProps> = ({
  templateId,
  initialValues,
  retailUnit,
  values,
  onChange: parentOnChange,
  onValidation: parentOnValidation,
  getFormState,
}) => {
  const { t } = useTranslation();

  const { onChange, validationHandlers } = usePartialFormModule({
    values: values ?? initialValues,
    numberOfPartialForms: 3,
    parentOnChange,
    parentOnValidation,
  });

  const {
    data: retailUnitData,
    isLoading: retailUnitIsLoading,
    error: retailUnitError,
  } = useGetRetailUnitQuery({ retailUnit });

  const [requiredNumberOfQuestions, setRequiredNumberOfQuestions] = useState(0);

  if (retailUnitIsLoading) {
    return (
      <Loading text={`${t('general-fetching')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (retailUnitError || !retailUnitData) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  return (
    <>
      <SwipeRulesPartialForm
        moduleName={MODULE_NAME}
        templateId={templateId}
        initialValues={initialValues}
        retailUnit={retailUnit}
        values={values}
        onChange={onChange}
        onValidationList={[validationHandlers[0], validationHandlers[1]]}
        setRequiredNumberOfQuestions={setRequiredNumberOfQuestions}
        getFormState={getFormState}
      />
      <SwipeTranslationsPartialForm
        moduleName={MODULE_NAME}
        templateId={templateId}
        initialValues={initialValues}
        retailUnit={retailUnit}
        retailUnitData={retailUnitData.retailUnit}
        values={values}
        onChange={onChange}
        onValidation={validationHandlers[2]}
        requiredNumberOfQuestions={requiredNumberOfQuestions}
        getFormState={getFormState}
      />
    </>
  );
};

export const module = {
  name: MODULE_NAME,
  transformer: SwipeFormTransformer,
  form: SwipeForm,
} as const;
